import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { register, deriveAgeBand, meKeys } from '../api/auth';
import { HatchlingPortrait } from '../companion/HatchlingPortrait';
import { ApiClientError } from '../api/client';
import { FormField } from './FormField';
import { deriveFormError } from './formErrors';
import { nameTooLong, validateName } from '../companion/nameValidation';
import { useOnline } from '../pwa/useOnline';
import { useMustRenameStore } from '../shell/mustRenameStore';

// Something, an @, a domain with at least one dot: the one shape `type=email` lets through that
// no mail server will ever deliver to (`a@b`).
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function RegisterScreen() {
  const nav = useNavigate();
  const isOnline = useOnline();
  const [fields, setFields] = useState({ email: '', password: '', displayName: '', birthDate: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [displayNameTouched, setDisplayNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const queryClient = useQueryClient();
  const m = useMutation({
    mutationFn: () => register({
      email: fields.email, password: fields.password,
      displayName: fields.displayName, ageBand: deriveAgeBand(fields.birthDate),
    }),
    // Decision #10: a brand-new registration is never grandfathered in practice (mustRename only
    // ever gets set on a pre-existing account by the one-time migration), but this still checks
    // the response honestly rather than assuming — same reasoning LoginScreen's own onSuccess
    // gives, and it keeps both entry points behaving identically if that assumption ever changes.
    onSuccess: (account) => {
      queryClient.setQueryData(meKeys.detail, account); // see LoginScreen: clears the gate's cached 401
      useMustRenameStore.getState().setMustRename(account.mustRename);
      nav(account.mustRename ? '/claim-handle' : '/habitat');
    },
  });
  const apiErr = m.error instanceof ApiClientError ? m.error : undefined;
  const emailTaken = apiErr?.code === 'email_taken';
  // QA 2026-09-23 onboard F1: `name_taken` belongs to the Keeper name field just as `email_taken`
  // belongs to Email — it used to fall through to the generic "Something went wrong" line.
  const { fieldErrors, formError } = deriveFormError(m.error, { email_taken: 'email', name_taken: 'displayName' });
  // C3 fix: instant client-side feedback mirroring server/src/main/java/com/projectpets/common/
  // NameRule.java exactly (same 2-20/charset rule AuthService.register() now enforces
  // server-side too) — same "local check first, server 400 still wins if it disagrees" pattern
  // as NewCompanionScreen's name field.
  const localDisplayNameError = validateName(fields.displayName);
  const displayNameError = (displayNameTouched || nameTooLong(fields.displayName) ? localDisplayNameError : undefined)
    ?? fieldErrors.displayName;
  // QA 2026-09-23 onboard F2: the browser's `type=email` accepts `a@b`, and the server registered
  // it — an address nobody can ever reach. The domain needs a dot; the server half is a separate
  // fix, so this stays "local check first, server still wins".
  const localEmailError = fields.email && !EMAIL_SHAPE.test(fields.email)
    ? 'Enter an email address like name@example.com.' : undefined;
  // The server's bean-validation 400 reads "Invalid field: email" — it also refuses the reserved
  // @projectpets.invalid domain, which passes the local shape check — so it gets a sentence.
  const serverEmailError = apiErr?.code === 'validation' && fieldErrors.email
    ? "That email address can't be used. Try another one." : fieldErrors.email;
  const emailError = (emailTouched ? localEmailError : undefined) ?? serverEmailError;
  const set = (k: keyof typeof fields) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFields(f => ({ ...f, [k]: e.target.value }));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setDisplayNameTouched(true);
    setEmailTouched(true);
    if (localDisplayNameError || localEmailError) return;
    m.mutate();
  }

  return (
    <main className="screen-form auth">
      <header className="auth-brand">
        <HatchlingPortrait code="sable" variant="joy" className="auth-brand-hero" />
        <h1>Become a Keeper</h1>
        <p>Takes a minute. Your first companion is next.</p>
      </header>
      <form onSubmit={handleSubmit}>
        <FormField label="Email" type="email" autoComplete="email" required
          value={fields.email} onChange={set('email')} onBlur={() => setEmailTouched(true)} disabled={m.isPending}
          error={emailError} />
        {emailTaken && <p>Already have an account? <Link to="/login">Log in instead</Link></p>}
        <FormField label="Password" type={showPassword ? 'text' : 'password'} autoComplete="new-password"
          minLength={10} required value={fields.password} onChange={set('password')} disabled={m.isPending}
          error={fieldErrors.password} hint="At least 10 characters."
          adornment={
            <button type="button" className="field-adornment" disabled={m.isPending}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword(s => !s)}>
              {showPassword ? 'Hide' : 'Show'}
            </button>
          } />
        <FormField label="Keeper name" minLength={2} required
          hint="Other Keepers see this, and it has to be unique."
          value={fields.displayName} onChange={set('displayName')} onBlur={() => setDisplayNameTouched(true)}
          disabled={m.isPending} error={displayNameError} />
        <FormField label="Birth date" type="date" required
          hint="Keeps the game age-appropriate. Only your age group is saved, never the date."
          value={fields.birthDate} onChange={set('birthDate')} disabled={m.isPending} />
        {formError && <p className="field-error" role="alert">{formError}</p>}
        <div className="actions">
          <button className="btn-primary" disabled={m.isPending || !isOnline}>
            {m.isPending ? 'Creating…' : 'Create account'}
          </button>
          {!isOnline && <p className="form-note">You&rsquo;re offline — reconnect to create an account.</p>}
          <p>Already a Keeper? <Link to="/login">Log in</Link></p>
        </div>
      </form>
    </main>
  );
}
